"use client";
import * as S from "./styles.ts";
import {
  Accordion2,
  Button,
  H1,
  IconBulletPoint,
  P,
  UniversalLink,
  VideoFrame,
} from "@template/components";
import Image, { ImageProps } from "@template/components/Image";
import { IButtonProps } from "@template/components/Button";
import UniversalMarkdown from "@template/blocks/universal-markdown.tsx";

export interface IBasicSectionProps {
  button?: {
    href: string;
  } & IButtonProps;
  header?: { title: string; subtitle?: string };
  description?: string;
  descriptionCtaText?: { label: string; href?: string }[];
  image?: ImageProps;
  videoUrl?: string;
  subtitle?: string;
  title?: string;
  flip?: boolean;
  bullets?: {
    text: string;
  }[];
  bulletsWithIcon?: boolean;
  accordionData?: { content: string; title: string }[];
  linkContent?: {
    label: string;
    href: string;
    target?: "_blank" | "_self";
    title?: string;
  }[];
  accordionHeader?: string;
  inverted?: boolean;
  anchorId?: string;
}

const BasicSectionComponent = ({
                                 title,
                                 subtitle,
                                 button,
                                 description,
                                 descriptionCtaText,
                                 image,
                                 videoUrl,
                                 flip,
                                 bullets,
                                 bulletsWithIcon = true,
                                 accordionData,
                                 linkContent,
                                 accordionHeader,
                                 header,
                                 inverted = false,
                                 anchorId,
                               }: IBasicSectionProps) => {
  return (
    <S.ItemWrapper
      $inverted={inverted}
      id={anchorId}
    >
      {(header?.title || header?.subtitle) && (
        <S.HeaderContainer>
          {header.title && <H1 as="h2">{header.title}</H1>}
          {header.subtitle && <P>{header.subtitle}</P>}
        </S.HeaderContainer>
      )}

      <S.ItemContainer $flip={flip}>
        {(subtitle ||
          title ||
          description ||
          descriptionCtaText ||
          bullets?.length > 0 ||
          button?.children) && (
          <S.InnerContainer>
            {subtitle && <S.Subtitle as="p">{subtitle}</S.Subtitle>}
            {title && <S.Title as="h2">{title}</S.Title>}
            {description && (
              <S.Paragaphs>
                {typeof description === "string" ? (
                  description
                    .split("\n")
                    .map((paragraph, index) => <P key={index}>{paragraph}</P>)
                ) : (
                  <P>
                    <UniversalMarkdown>{description}</UniversalMarkdown>
                  </P>
                )}
              </S.Paragaphs>
            )}

            {descriptionCtaText && (
              <S.Description>
                {descriptionCtaText.map((segment, index) =>
                  segment.href ? (
                    <UniversalLink
                      key={index}
                      href={segment.href}
                    >
                      {segment.label}
                    </UniversalLink>
                  ) : (
                    segment.label
                  )
                )}
              </S.Description>
            )}

            {bullets?.length > 0 && (
              <S.BulletPointContainer>
                {bullets.map((bullet, index) => (
                  <IconBulletPoint
                    key={index}
                    item={{
                      label: bullet.text,
                    }}
                    withIcon={bulletsWithIcon}
                  />
                ))}
              </S.BulletPointContainer>
            )}

            {(button?.children || anchorId) && (
              <S.ButtonsContainer>
                {button?.children && (
                  <Button
                    {...button}
                    size="md"
                    navigation={{
                      href: button.href,
                    }}
                    title={button.title}
                  >
                    {button.children}
                  </Button>
                )}

                {anchorId && (
                  <Button
                    variant="outline"
                    size="md"
                    navigation={{
                      href: "#anchorMenu",
                    }}
                  >
                    Back to top
                  </Button>
                )}
              </S.ButtonsContainer>
            )}
          </S.InnerContainer>
        )}

        {accordionData?.length > 0 && (
          <S.AccordionContainer>
            <Accordion2
              items={accordionData}
              activeColor="primary"
              type="exclusive"
              withArrow={true}
            />
          </S.AccordionContainer>
        )}

        {(linkContent?.length > 0 || accordionHeader) && (
          <S.AccordionContainer>
            {accordionHeader && <S.Title>{accordionHeader}</S.Title>}
            {linkContent.map((link, index) => (
              <UniversalLink
                key={index}
                href={link.href}
                target={link.target}
                title={link.title}
              >
                <S.LinkContentItem>{link.label}</S.LinkContentItem>
              </UniversalLink>
            ))}
          </S.AccordionContainer>
        )}

        {image?.src && (
          <S.InnerContainer>
            <Image
              src={image.src}
              alt={image.alt}
              width={900}
              height={600}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </S.InnerContainer>
        )}

        {videoUrl && (
          <S.VideoInnerContainer>
            <VideoFrame
              src={videoUrl}
              small
            />
          </S.VideoInnerContainer>
        )}
      </S.ItemContainer>
    </S.ItemWrapper>
  );
};

export default BasicSectionComponent;
